import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Calendar } from "@/components/ui/calendar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { 
  Plus, 
  Search, 
  Phone, 
  Mail, 
  MessageCircle,
  Clock,
  X,
  Globe,
  Facebook,
  Linkedin,
  PhoneCall,
  PhoneMissed,
  Users,
  RefreshCw,
  Send,
  Calendar as CalendarIcon,
  FileText,
  Activity,
  CheckCircle,
  XCircle,
  AlertCircle,
  MoreHorizontal,
  UserPlus,
  MessageSquare,
  History,
  Target,
  TrendingUp,
  Tent,
  ShoppingCart,
  UserCheck,
  Link,
  HelpCircle,
  Trash2,
  Edit2,
  MapPin,
  Copy,
  Bot,
  Headphones,
  Zap
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";

const API = process.env.REACT_APP_BACKEND_URL;

// Disposition colors
const dispositionColors = {
  not_contacted: "bg-gray-100 text-gray-800",
  call_not_picked: "bg-orange-100 text-orange-800",
  call_later: "bg-blue-100 text-blue-800",
  not_interested: "bg-red-100 text-red-800",
  wrong_number: "bg-red-100 text-red-800",
  interested: "bg-green-100 text-green-800",
  appointment_booked: "bg-purple-100 text-purple-800",
  converted: "bg-emerald-100 text-emerald-800",
  dnd: "bg-red-100 text-red-800",
  language_barrier: "bg-yellow-100 text-yellow-800",
  already_customer: "bg-cyan-100 text-cyan-800",
  callback_requested: "bg-blue-100 text-blue-800",
  busy: "bg-orange-100 text-orange-800",
  voicemail: "bg-gray-100 text-gray-800",
  no_service_zone: "bg-slate-100 text-slate-800",
  doctor_patient: "bg-rose-100 text-rose-800",
  budget_issue: "bg-orange-100 text-orange-800",
  camp_registration: "bg-teal-100 text-teal-800",
};

const sourceTabs = [
  { value: "all", label: "All", icon: Users, color: "text-slate-600" },
  { value: "website", label: "Website", icon: Globe, color: "text-blue-600" },
  { value: "whatsapp", label: "WhatsApp", icon: MessageCircle, color: "text-green-600" },
  { value: "facebook", label: "Facebook", icon: Facebook, color: "text-blue-500" },
  { value: "google_ads", label: "Google", icon: Globe, color: "text-red-500" },
  { value: "linkedin", label: "LinkedIn", icon: Linkedin, color: "text-blue-700" },
  { value: "ivr", label: "IVR", icon: PhoneCall, color: "text-purple-600" },
  { value: "missed_call", label: "Missed Call", icon: PhoneMissed, color: "text-orange-600" },
  { value: "camp", label: "Camp", icon: Tent, color: "text-teal-600" },
  { value: "indiamart", label: "IndiaMart", icon: ShoppingCart, color: "text-blue-800" },
  { value: "referral", label: "Referral", icon: UserCheck, color: "text-green-700" },
  { value: "manual", label: "Manual", icon: FileText, color: "text-slate-500" },
  { value: "email", label: "Email", icon: Mail, color: "text-amber-600" },
  { value: "external", label: "External", icon: Link, color: "text-indigo-600" },
  { value: "unknown", label: "Other", icon: HelpCircle, color: "text-gray-500" },
];

// Sort icon component - must be outside main component
const SortIcon = ({ field, sortField, sortOrder }) => {
  if (sortField !== field) return <span className="text-slate-300 ml-1">↕</span>;
  return <span className="text-cyan-700 ml-1">{sortOrder === "asc" ? "↑" : "↓"}</span>;
};

export default function LeadsPageEnterprise({ user, logout }) {
  const [searchParams] = useSearchParams();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterSource, setFilterSource] = useState(searchParams.get("source") || "all");
  const [filterDisposition, setFilterDisposition] = useState(searchParams.get("disposition") || "");
  const [filterAgent, setFilterAgent] = useState(searchParams.get("agent") || "all");
  const [leadCounts, setLeadCounts] = useState({});
  const [dispositions, setDispositions] = useState([]);
  const [selectedLead, setSelectedLead] = useState(null);
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [activities, setActivities] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [newDisposition, setNewDisposition] = useState("");
  const [dispositionNotes, setDispositionNotes] = useState("");
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  const [followUpDate, setFollowUpDate] = useState(null);
  const [followUpTime, setFollowUpTime] = useState("10:00");
  const [followUpReason, setFollowUpReason] = useState("");
  const [rescheduleFollowUpId, setRescheduleFollowUpId] = useState(null);
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [whatsappMessage, setWhatsappMessage] = useState("");
  const [userRole, setUserRole] = useState("caller");
  const [currentUser, setCurrentUser] = useState(null);
  const [sortField, setSortField] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");
  
  // Date range filter
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  
  // New state for Add Lead and Assignment
  const [showAddLeadModal, setShowAddLeadModal] = useState(false);
  const [newLead, setNewLead] = useState({ name: "", phone: "", email: "", source: "manual", notes: "", assigned_to: "auto", city: "" });
  const [teamUsers, setTeamUsers] = useState([]);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignToUser, setAssignToUser] = useState("");
  const [sendingWhatsApp, setSendingWhatsApp] = useState(false);
  
  // Bulk selection state
  const [selectedLeadIds, setSelectedLeadIds] = useState([]);
  const [showBulkAssignModal, setShowBulkAssignModal] = useState(false);
  const [bulkAssignUser, setBulkAssignUser] = useState("");
  const [bulkAssigning, setBulkAssigning] = useState(false);
  
  // Computed: Filter team users based on hierarchy for assignment dropdowns
  const assignableUsers = (() => {
    if (!currentUser) return [];
    
    // If teamUsers is empty (e.g., caller can't access /api/team/users), 
    // still allow the user to assign to themselves
    if (teamUsers.length === 0) {
      return [currentUser];
    }
    
    return teamUsers.filter(u => {
      // Super Admin and Admin can assign to anyone
      if (["super_admin", "admin"].includes(currentUser.role)) return true;
      // Team Lead can only assign to callers in their team or themselves
      if (currentUser.role === "team_lead") {
        return u.team_lead_id === currentUser.user_id || u.user_id === currentUser.user_id;
      }
      // Caller can only assign to themselves
      if (currentUser.role === "caller") {
        return u.user_id === currentUser.user_id;
      }
      return false;
    });
  })();

  // Computed: Filter users for the Agent Filter dropdown based on hierarchy (Option A)
  // Team Leads see only their team members, Admins/Super Admins see everyone
  const filterableAgents = (() => {
    if (!currentUser) return [];
    
    if (teamUsers.length === 0) {
      return [currentUser];
    }
    
    return teamUsers.filter(u => {
      // Super Admin and Admin can filter by anyone
      if (["super_admin", "admin"].includes(currentUser.role)) return true;
      // Team Lead can only see/filter by callers in their team or themselves
      if (currentUser.role === "team_lead") {
        return u.team_lead_id === currentUser.user_id || u.user_id === currentUser.user_id;
      }
      // Caller can only see themselves
      if (currentUser.role === "caller") {
        return u.user_id === currentUser.user_id;
      }
      return false;
    });
  })();
  
  // Cities list
  const [cities, setCities] = useState([]);
  
  // Duplicates management
  const [showDuplicatesModal, setShowDuplicatesModal] = useState(false);
  const [duplicates, setDuplicates] = useState([]);
  const [loadingDuplicates, setLoadingDuplicates] = useState(false);
  
  // Edit name state
  const [editingName, setEditingName] = useState(false);
  const [editedName, setEditedName] = useState("");
  
  // WhatsApp Conversation state
  const [conversationHistory, setConversationHistory] = useState([]);
  const [loadingConversation, setLoadingConversation] = useState(false);
  
  // Convert to Customer state
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [supporthubMasters, setSupporthubMasters] = useState(null);
  const [loadingMasters, setLoadingMasters] = useState(false);
  const [convertForm, setConvertForm] = useState({
    name: "",
    mobileno: "",
    email: "",
    clinic: "",
    appointmenttype: "",
    appointment: "",
    consultant: "",
    subbrand: "",
    subject: "",
    comment: ""
  });
  const [converting, setConverting] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalLeads, setTotalLeads] = useState(0);
  const isInitialMount = useRef(true);

  // Reset page to 1 when filters change (but not on initial mount)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    setCurrentPage(1);
  }, [filterSource, filterDisposition, searchQuery, filterAgent]);

  // Fetch data when pagination/sorting/filters change
  useEffect(() => {
    fetchLeads();
    fetchLeadCounts();
  }, [filterSource, filterDisposition, searchQuery, sortField, sortOrder, filterAgent, currentPage, pageSize, dateFrom, dateTo]);

  // Initial data load (only once)
  useEffect(() => {
    fetchTeamUsers();
    fetchDispositions();
    fetchUserRole();
    fetchCities();
  }, []);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchLeads();
      fetchLeadCounts();
    }, 30000);
    return () => clearInterval(interval);
  }, [filterSource, filterDisposition, searchQuery, filterAgent, currentPage, pageSize]);

  const fetchTeamUsers = async () => {
    try {
      const response = await axios.get(`${API}/api/team/users`, { withCredentials: true });
      setTeamUsers(response.data.users || []);
    } catch (error) {
      console.error("Failed to fetch team users");
    }
  };

  const fetchCities = async () => {
    try {
      const response = await axios.get(`${API}/api/leads/meta/cities`, { withCredentials: true });
      setCities(response.data.cities || []);
    } catch (error) {
      console.error("Failed to fetch cities");
    }
  };

  const fetchDuplicates = async () => {
    try {
      setLoadingDuplicates(true);
      const response = await axios.get(`${API}/api/leads/duplicates`, { withCredentials: true });
      setDuplicates(response.data.duplicates || []);
    } catch (error) {
      toast.error("Failed to fetch duplicates");
    } finally {
      setLoadingDuplicates(false);
    }
  };

  const handleDeleteLead = async (leadId) => {
    if (!window.confirm("Are you sure you want to delete this lead? This action cannot be undone.")) {
      return;
    }
    try {
      await axios.delete(`${API}/api/leads/${leadId}`, { withCredentials: true });
      toast.success("Lead deleted successfully");
      fetchLeads();
      setSelectedLead(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete lead");
    }
  };

  const handleMergeDuplicates = async (primaryId, duplicateIds) => {
    try {
      await axios.post(`${API}/api/leads/merge-duplicates`, {
        primary_lead_id: primaryId,
        duplicate_lead_ids: duplicateIds
      }, { withCredentials: true });
      toast.success("Duplicates merged successfully");
      fetchDuplicates();
      fetchLeads();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to merge duplicates");
    }
  };

  const handleUpdateName = async () => {
    if (!editedName.trim()) {
      toast.error("Name cannot be empty");
      return;
    }
    try {
      await axios.put(`${API}/api/leads/${selectedLead.lead_id}`, {
        name: editedName.trim()
      }, { withCredentials: true });
      toast.success("Name updated successfully");
      setSelectedLead({ ...selectedLead, name: editedName.trim() });
      setEditingName(false);
      fetchLeads();
    } catch (error) {
      toast.error("Failed to update name");
    }
  };

  const handleUpdateCity = async (city) => {
    try {
      await axios.put(`${API}/api/leads/${selectedLead.lead_id}`, { city }, { withCredentials: true });
      toast.success("City updated");
      setSelectedLead({ ...selectedLead, city });
      fetchLeads();
    } catch (error) {
      toast.error("Failed to update city");
    }
  };

  const handleUpdateSource = async (source) => {
    try {
      await axios.put(`${API}/api/leads/${selectedLead.lead_id}/source`, { source }, { withCredentials: true });
      toast.success("Source updated");
      setSelectedLead({ ...selectedLead, source });
      fetchLeads();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update source");
    }
  };

  const fetchUserRole = async () => {
    try {
      const response = await axios.get(`${API}/api/team/my-role`, { withCredentials: true });
      const userData = response.data.user;
      setUserRole(userData?.role || "caller");
      setCurrentUser(userData);
    } catch (error) {
      console.error("Failed to fetch user role");
    }
  };

  const fetchDispositions = async () => {
    try {
      const response = await axios.get(`${API}/api/leads/meta/dispositions`, { withCredentials: true });
      setDispositions(response.data.dispositions || []);
    } catch (error) {
      console.error("Failed to fetch dispositions");
    }
  };

  const fetchLeads = async () => {
    try {
      setLoading(true);
      const skip = (currentPage - 1) * pageSize;
      let url = `${API}/api/leads?limit=${pageSize}&skip=${skip}`;
      if (filterSource && filterSource !== "all") url += `&source=${filterSource}`;
      if (filterDisposition) url += `&disposition=${filterDisposition}`;
      if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;
      if (sortField) url += `&sort_by=${sortField}&sort_order=${sortOrder}`;
      if (filterAgent && filterAgent !== "all") url += `&assigned_to=${filterAgent}`;
      if (dateFrom) url += `&date_from=${dateFrom}`;
      if (dateTo) url += `&date_to=${dateTo}`;
      
      const response = await axios.get(url, { withCredentials: true });
      let filteredLeads = response.data.leads || [];
      
      // Client-side sorting as fallback
      filteredLeads.sort((a, b) => {
        let aVal = a[sortField] || "";
        let bVal = b[sortField] || "";
        if (sortField === "created_at" || sortField === "last_contacted_at") {
          aVal = new Date(aVal || 0).getTime();
          bVal = new Date(bVal || 0).getTime();
        }
        if (sortOrder === "asc") {
          return aVal > bVal ? 1 : -1;
        }
        return aVal < bVal ? 1 : -1;
      });
      
      setLeads(filteredLeads);
      setTotalLeads(response.data.total || 0);
    } catch (error) {
      console.error("Failed to fetch leads");
    } finally {
      setLoading(false);
    }
  };

  // Add new lead
  const handleAddLead = async () => {
    if (!newLead.name || !newLead.phone) {
      toast.error("Name and phone are required");
      return;
    }
    try {
      const leadData = {
        name: newLead.name,
        phone: newLead.phone,
        email: newLead.email || null,
        source: newLead.source,
        notes: newLead.notes ? [{ text: newLead.notes, author: user?.name || "Agent" }] : []
      };
      // Add assigned_to if selected (not "auto" which means use auto-allocation)
      if (newLead.assigned_to && newLead.assigned_to !== "auto") {
        leadData.assigned_to = newLead.assigned_to;
      }
      await axios.post(`${API}/api/leads`, leadData, { withCredentials: true });
      toast.success("Lead added successfully!");
      setShowAddLeadModal(false);
      setNewLead({ name: "", phone: "", email: "", source: "manual", notes: "", assigned_to: "auto" });
      fetchLeads();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add lead");
    }
  };

  // Assign lead to agent
  const handleAssignLead = async () => {
    if (!assignToUser || !selectedLead) return;
    try {
      await axios.put(`${API}/api/leads/${selectedLead.lead_id}`, {
        assigned_to: assignToUser
      }, { withCredentials: true });
      toast.success("Lead assigned successfully!");
      setShowAssignModal(false);
      setAssignToUser("");
      fetchLeads();
      // Update selected lead
      setSelectedLead({ ...selectedLead, assigned_to: assignToUser });
    } catch (error) {
      toast.error("Failed to assign lead");
    }
  };

  // Bulk assign leads
  const handleBulkAssign = async () => {
    if (!bulkAssignUser || selectedLeadIds.length === 0) return;
    setBulkAssigning(true);
    try {
      let successCount = 0;
      for (const leadId of selectedLeadIds) {
        try {
          await axios.put(`${API}/api/leads/${leadId}`, {
            assigned_to: bulkAssignUser
          }, { withCredentials: true });
          successCount++;
        } catch (err) {
          console.error(`Failed to assign lead ${leadId}:`, err);
        }
      }
      toast.success(`Successfully assigned ${successCount} of ${selectedLeadIds.length} leads`);
      setShowBulkAssignModal(false);
      setBulkAssignUser("");
      setSelectedLeadIds([]);
      fetchLeads();
    } catch (error) {
      toast.error("Failed to bulk assign leads");
    } finally {
      setBulkAssigning(false);
    }
  };

  // Toggle lead selection
  const toggleLeadSelection = (leadId) => {
    setSelectedLeadIds(prev => 
      prev.includes(leadId) 
        ? prev.filter(id => id !== leadId)
        : [...prev, leadId]
    );
  };

  // Select all visible leads
  const toggleSelectAll = () => {
    if (selectedLeadIds.length === leads.length) {
      setSelectedLeadIds([]);
    } else {
      setSelectedLeadIds(leads.map(l => l.lead_id));
    }
  };

  // Fetch WhatsApp conversation history
  const fetchConversationHistory = async (phone) => {
    if (!phone) return;
    setLoadingConversation(true);
    try {
      const response = await axios.get(`${API}/api/whatsapp/messages/${phone}`, { withCredentials: true });
      setConversationHistory(response.data.messages || []);
    } catch (error) {
      console.error("Failed to fetch conversation:", error);
      setConversationHistory([]);
    } finally {
      setLoadingConversation(false);
    }
  };

  // Open WhatsApp modal and fetch conversation
  const openWhatsAppChat = (lead) => {
    setSelectedLead(lead);
    setWhatsappMessage("");
    setShowWhatsAppModal(true);
    fetchConversationHistory(lead.phone);
  };

  // Send WhatsApp reply
  const handleSendWhatsAppReply = async () => {
    if (!whatsappMessage.trim() || !selectedLead) return;
    setSendingWhatsApp(true);
    try {
      await axios.post(`${API}/api/whatsapp/send`, {
        phone_number: selectedLead.phone,
        message_text: whatsappMessage
      }, { withCredentials: true });
      toast.success("WhatsApp message sent!");
      setWhatsappMessage("");
      // Refresh conversation history
      fetchConversationHistory(selectedLead.phone);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send WhatsApp message");
    } finally {
      setSendingWhatsApp(false);
    }
  };

  // Fetch Supporthub master data
  const fetchSupporthubMasters = async () => {
    if (supporthubMasters) return; // Already fetched
    setLoadingMasters(true);
    try {
      const response = await axios.get(`${API}/api/supporthub/masters`, { withCredentials: true });
      setSupporthubMasters(response.data);
    } catch (error) {
      toast.error("Failed to fetch Supporthub data");
    } finally {
      setLoadingMasters(false);
    }
  };

  // Open convert to customer modal
  const openConvertModal = (lead) => {
    setSelectedLead(lead);
    // Pre-fill form with lead data
    setConvertForm({
      name: lead.name || "",
      mobileno: lead.phone?.replace(/^\+91/, "").replace(/\s/g, "") || "",
      email: lead.email || "",
      clinic: "",
      appointmenttype: "2", // Default to Clinic Visit
      appointment: "",
      consultant: "",
      subbrand: mapLeadSourceToSubbrand(lead.source),
      subject: `Lead converted from ${lead.source || "CRM"}`,
      comment: lead.notes?.length > 0 ? lead.notes[lead.notes.length - 1]?.text || "" : ""
    });
    setShowConvertModal(true);
    fetchSupporthubMasters();
  };

  // Map lead source to Supporthub subbrand ID
  const mapLeadSourceToSubbrand = (source) => {
    const mapping = {
      "website": "168",
      "facebook": "5",
      "google_ads": "81",
      "ivr": "7",
      "missed_call": "6",
      "linkedin": "146",
      "walk_in": "2",
      "camp": "1",
      "manual": "2"
    };
    return mapping[source] || "2"; // Default to Walk In
  };

  // Handle convert to customer
  const handleConvertToCustomer = async () => {
    // Validate required fields
    if (!convertForm.name || !convertForm.mobileno || !convertForm.clinic || !convertForm.appointmenttype || !convertForm.appointment || !convertForm.consultant) {
      toast.error("Please fill all required fields");
      return;
    }

    setConverting(true);
    try {
      // Get names for storing
      const clinicName = supporthubMasters?.clinics?.find(c => String(c.clinic_id) === convertForm.clinic)?.clinic_name || "";
      const appointmentTypeName = supporthubMasters?.appointment_types?.find(a => String(a.appointment_id) === convertForm.appointmenttype)?.appointment_name || "";
      const consultantName = supporthubMasters?.consultants?.find(c => String(c.consultant_id) === convertForm.consultant)?.consultant_name || "";
      const subbrandName = supporthubMasters?.subsources?.find(s => String(s.sub_source_id) === convertForm.subbrand)?.sub_source_name || "";

      const response = await axios.post(`${API}/api/supporthub/create-appointment`, {
        lead_id: selectedLead.lead_id,
        ...convertForm,
        clinic_name: clinicName,
        appointment_type_name: appointmentTypeName,
        consultant_name: consultantName,
        subbrand_name: subbrandName
      }, { withCredentials: true });

      if (response.data.success) {
        toast.success(`Customer created! Ticket: ${response.data.ticket_no}`);
        setShowConvertModal(false);
        setShowLeadModal(false);
        fetchLeads();
        fetchLeadCounts();
      } else {
        toast.error(response.data.message || "Failed to create customer");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to convert to customer");
    } finally {
      setConverting(false);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("desc");
    }
  };

  const fetchLeadCounts = async () => {
    try {
      const counts = { all: 0 };
      // Include all sources from sourceTabs
      const sources = sourceTabs
        .filter(tab => tab.value !== 'all')
        .map(tab => tab.value);
      
      // Build base URL with agent filter if selected
      let baseUrl = `${API}/api/leads?limit=1`;
      if (filterAgent && filterAgent !== "all") {
        baseUrl += `&assigned_to=${filterAgent}`;
      }
      
      const totalRes = await axios.get(baseUrl, { withCredentials: true });
      counts.all = totalRes.data.total || 0;
      
      const promises = sources.map(source => {
        let url = `${API}/api/leads?source=${source}&limit=1`;
        if (filterAgent && filterAgent !== "all") {
          url += `&assigned_to=${filterAgent}`;
        }
        return axios.get(url, { withCredentials: true })
          .then(res => ({ source, count: res.data.total || 0 }))
          .catch(() => ({ source, count: 0 }));
      });
      
      const results = await Promise.all(promises);
      results.forEach(r => { counts[r.source] = r.count; });
      
      setLeadCounts(counts);
    } catch (error) {
      console.error("Failed to fetch lead counts");
    }
  };

  const fetchActivities = async (leadId) => {
    try {
      const response = await axios.get(`${API}/api/leads/${leadId}/activities`, { withCredentials: true });
      setActivities(response.data.activities || []);
    } catch (error) {
      console.error("Failed to fetch activities");
    }
  };

  const openLeadModal = (lead) => {
    setSelectedLead(lead);
    setNewDisposition(lead.disposition || "not_contacted");
    setShowLeadModal(true);
    fetchActivities(lead.lead_id);
  };

  const updateDisposition = async () => {
    if (!selectedLead || !newDisposition) return;
    
    try {
      await axios.put(`${API}/api/leads/${selectedLead.lead_id}/disposition`, {
        disposition: newDisposition,
        notes: dispositionNotes
      }, { withCredentials: true });
      
      toast.success("Disposition updated");
      setDispositionNotes("");
      fetchLeads();
      fetchActivities(selectedLead.lead_id);
      
      // Update selected lead
      setSelectedLead(prev => ({ ...prev, disposition: newDisposition }));
    } catch (error) {
      toast.error("Failed to update disposition");
    }
  };

  const addNote = async () => {
    if (!selectedLead || !newNote.trim()) return;
    
    try {
      await axios.post(`${API}/api/leads/${selectedLead.lead_id}/notes`, {
        text: newNote
      }, { withCredentials: true });
      
      toast.success("Note added");
      setNewNote("");
      fetchActivities(selectedLead.lead_id);
      
      // Refresh lead data
      const response = await axios.get(`${API}/api/leads/${selectedLead.lead_id}`, { withCredentials: true });
      setSelectedLead(response.data);
    } catch (error) {
      toast.error("Failed to add note");
    }
  };

  const createFollowUp = async () => {
    if (!selectedLead || !followUpDate) return;
    
    try {
      // If rescheduleFollowUpId is set, we're rescheduling an existing follow-up
      if (rescheduleFollowUpId) {
        await axios.patch(`${API}/api/followups/${rescheduleFollowUpId}/reschedule`, {
          scheduled_date: format(followUpDate, "yyyy-MM-dd"),
          scheduled_time: followUpTime,
          reason: followUpReason || "Rescheduled follow-up"
        }, { withCredentials: true });
        toast.success("Follow-up rescheduled");
      } else {
        await axios.post(`${API}/api/leads/${selectedLead.lead_id}/followup`, {
          scheduled_date: format(followUpDate, "yyyy-MM-dd"),
          scheduled_time: followUpTime,
          reason: followUpReason || "Follow up call"
        }, { withCredentials: true });
        toast.success("Follow-up scheduled");
      }
      
      setShowFollowUpModal(false);
      setFollowUpDate(null);
      setFollowUpReason("");
      setRescheduleFollowUpId(null);
      fetchActivities(selectedLead.lead_id);
      // Refresh lead details to show updated follow-ups
      const leadRes = await axios.get(`${API}/api/leads/${selectedLead.lead_id}`, { withCredentials: true });
      setSelectedLead(leadRes.data);
    } catch (error) {
      toast.error(rescheduleFollowUpId ? "Failed to reschedule follow-up" : "Failed to create follow-up");
    }
  };

  const sendWhatsApp = async () => {
    if (!selectedLead || !whatsappMessage.trim()) return;
    
    try {
      await axios.post(`${API}/api/leads/${selectedLead.lead_id}/whatsapp`, {
        message: whatsappMessage
      }, { withCredentials: true });
      
      toast.success("WhatsApp message sent");
      setShowWhatsAppModal(false);
      setWhatsappMessage("");
      fetchActivities(selectedLead.lead_id);
    } catch (error) {
      toast.error("Failed to send WhatsApp");
    }
  };

  const convertToCustomer = async () => {
    if (!selectedLead) return;
    
    try {
      await axios.post(`${API}/api/leads/${selectedLead.lead_id}/convert`, {}, { withCredentials: true });
      toast.success("Lead converted to customer");
      setShowLeadModal(false);
      fetchLeads();
    } catch (error) {
      toast.error("Failed to convert lead");
    }
  };

  const getDispositionBadge = (disposition) => {
    const colorClass = dispositionColors[disposition] || dispositionColors.not_contacted;
    const disp = dispositions.find(d => d.id === disposition);
    return (
      <Badge className={colorClass}>
        {disp?.label || disposition || "Not Contacted"}
      </Badge>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      return format(new Date(dateStr), "MMM dd, yyyy HH:mm");
    } catch {
      return dateStr;
    }
  };

  // Get the most recent note date for display
  const getLastNoteDate = (lead) => {
    if (!lead.notes || lead.notes.length === 0) return null;
    const lastNote = lead.notes[lead.notes.length - 1];
    return lastNote.timestamp || lastNote.created_at || null;
  };

  return (
    <DashboardLayout user={user} logout={logout}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-[Manrope]">Lead Management</h1>
            <p className="text-slate-600 mt-1">Enterprise CRM - Manage, track and convert leads</p>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => { fetchLeads(); fetchLeadCounts(); toast.success("Refreshed!"); }}
              data-testid="refresh-leads-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Source Tabs */}
        <div className="overflow-x-auto">
          <div className="flex gap-2 pb-2">
            {sourceTabs.map((tab) => {
              const Icon = tab.icon;
              const count = leadCounts[tab.value] || 0;
              const isActive = filterSource === tab.value;
              return (
                <Button
                  key={tab.value}
                  variant={isActive ? "default" : "outline"}
                  className={`flex items-center gap-2 ${isActive ? "bg-cyan-700" : ""}`}
                  onClick={() => setFilterSource(tab.value)}
                  data-testid={`source-tab-${tab.value}`}
                >
                  <Icon className={`w-4 h-4 ${isActive ? "text-white" : tab.color}`} />
                  <span>{tab.label}</span>
                  <Badge variant="secondary" className="ml-1 text-xs">
                    {count}
                  </Badge>
                </Button>
              );
            })}
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                <Input
                  placeholder="Search by name, phone, email..."
                  className="pl-10"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  data-testid="lead-search-input"
                />
              </div>
              <Select value={filterDisposition || "all"} onValueChange={(val) => setFilterDisposition(val === "all" ? "" : val)}>
                <SelectTrigger className="w-[200px]" data-testid="disposition-filter">
                  <SelectValue placeholder="Filter by Disposition" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Dispositions</SelectItem>
                  {dispositions.map((d) => (
                    <SelectItem key={d.id} value={d.id}>{d.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {/* Agent Filter - Hierarchy-based (Team Leads see only their team) */}
              <Select value={filterAgent} onValueChange={setFilterAgent}>
                <SelectTrigger className="w-[180px]" data-testid="agent-filter">
                  <SelectValue placeholder="Filter by Agent" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Agents</SelectItem>
                  {filterableAgents.map((u) => (
                    <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {/* Date Range Filter */}
              <div className="flex items-center gap-2">
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="w-[140px] h-10 text-sm"
                  placeholder="From"
                  data-testid="date-from"
                />
                <span className="text-slate-400 text-sm">to</span>
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="w-[140px] h-10 text-sm"
                  placeholder="To"
                  data-testid="date-to"
                />
                {(dateFrom || dateTo) && (
                  <Button variant="ghost" size="sm" onClick={() => { setDateFrom(""); setDateTo(""); }} className="text-slate-500 px-2">
                    Clear
                  </Button>
                )}
              </div>
              {/* Add Lead Button */}
              <Button 
                onClick={() => setShowAddLeadModal(true)} 
                className="bg-cyan-700 hover:bg-cyan-800"
                data-testid="add-lead-btn"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Add Lead
              </Button>
              {/* Find Duplicates Button (Admin only) */}
              {["admin", "super_admin"].includes(userRole) && (
                <Button 
                  variant="outline" 
                  onClick={() => { setShowDuplicatesModal(true); fetchDuplicates(); }}
                  className="border-orange-300 text-orange-700 hover:bg-orange-50"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Find Duplicates
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Bulk Actions Bar */}
        {selectedLeadIds.length > 0 && (
          <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Badge className="bg-cyan-700">{selectedLeadIds.length} selected</Badge>
              <span className="text-sm text-slate-600">leads selected</span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setSelectedLeadIds([])}
              >
                Clear Selection
              </Button>
              <Button
                size="sm"
                className="bg-cyan-700 hover:bg-cyan-800"
                onClick={() => setShowBulkAssignModal(true)}
                data-testid="bulk-assign-btn"
              >
                <Users className="w-4 h-4 mr-2" />
                Bulk Assign
              </Button>
            </div>
          </div>
        )}

        {/* Leads Table */}
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full data-table">
                <thead>
                  <tr>
                    <th className="w-10">
                      <input
                        type="checkbox"
                        className="w-4 h-4 rounded border-slate-300"
                        checked={leads.length > 0 && selectedLeadIds.length === leads.length}
                        onChange={toggleSelectAll}
                        data-testid="select-all-checkbox"
                      />
                    </th>
                    <th className="cursor-pointer hover:bg-slate-100" onClick={() => handleSort("name")}>
                      Lead Name <SortIcon field="name" sortField={sortField} sortOrder={sortOrder} />
                    </th>
                    <th className="cursor-pointer hover:bg-slate-100" onClick={() => handleSort("source")}>
                      Source <SortIcon field="source" sortField={sortField} sortOrder={sortOrder} />
                    </th>
                    <th className="cursor-pointer hover:bg-slate-100" onClick={() => handleSort("disposition")}>
                      Disposition <SortIcon field="disposition" sortField={sortField} sortOrder={sortOrder} />
                    </th>
                    <th className="cursor-pointer hover:bg-slate-100" onClick={() => handleSort("created_at")}>
                      Created <SortIcon field="created_at" sortField={sortField} sortOrder={sortOrder} />
                    </th>
                    <th className="cursor-pointer hover:bg-slate-100" onClick={() => handleSort("assigned_to_name")}>
                      Assigned To <SortIcon field="assigned_to_name" sortField={sortField} sortOrder={sortOrder} />
                    </th>
                    <th className="cursor-pointer hover:bg-slate-100" onClick={() => handleSort("last_contacted_at")}>
                      Last Contact <SortIcon field="last_contacted_at" sortField={sortField} sortOrder={sortOrder} />
                    </th>
                    <th>Next Follow-up</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={9} className="text-center py-8">
                        <div className="flex justify-center">
                          <div className="w-6 h-6 border-2 border-cyan-700 border-t-transparent rounded-full animate-spin" />
                        </div>
                      </td>
                    </tr>
                  ) : leads.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="text-center py-8 text-slate-500">
                        No leads found
                      </td>
                    </tr>
                  ) : (
                    leads.map((lead) => (
                      <tr 
                        key={lead.lead_id} 
                        className={`cursor-pointer hover:bg-slate-50 ${selectedLeadIds.includes(lead.lead_id) ? 'bg-cyan-50' : ''}`}
                        onClick={() => openLeadModal(lead)}
                        data-testid={`lead-row-${lead.lead_id}`}
                      >
                        <td onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            className="w-4 h-4 rounded border-slate-300"
                            checked={selectedLeadIds.includes(lead.lead_id)}
                            onChange={() => toggleLeadSelection(lead.lead_id)}
                            data-testid={`lead-checkbox-${lead.lead_id}`}
                          />
                        </td>
                        <td>
                          <div>
                            <p className="font-medium text-slate-900">{lead.name || "Unknown"}</p>
                            <p className="text-sm text-slate-500 flex items-center gap-1">
                              <Phone className="w-3 h-3" /> {lead.phone}
                            </p>
                            {lead.email && (
                              <p className="text-sm text-slate-500 flex items-center gap-1">
                                <Mail className="w-3 h-3" /> {lead.email}
                              </p>
                            )}
                          </div>
                        </td>
                        <td>
                          <Badge variant="outline" className="capitalize">
                            {lead.source?.replace("_", " ")}
                          </Badge>
                          {lead.camp_name && (
                            <p className="text-xs text-slate-500 mt-1">{lead.camp_name}</p>
                          )}
                        </td>
                        <td>{getDispositionBadge(lead.disposition)}</td>
                        <td>
                          <div className="text-sm">
                            {lead.created_at ? (
                              <>
                                <p className="font-medium">{format(new Date(lead.created_at), "dd MMM yyyy")}</p>
                                <p className="text-slate-500">{format(new Date(lead.created_at), "hh:mm a")}</p>
                              </>
                            ) : (
                              <span className="text-slate-400">-</span>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="text-sm">
                            {lead.assigned_to_name ? (
                              <span className="text-slate-700">{lead.assigned_to_name}</span>
                            ) : (
                              <span className="text-slate-400">Unassigned</span>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="text-sm">
                            {(() => {
                              // Priority: last_contacted_at > last note date
                              const lastNoteDate = getLastNoteDate(lead);
                              const displayDate = lead.last_contacted_at || lastNoteDate;
                              
                              if (displayDate) {
                                return (
                                  <>
                                    <p>{format(new Date(displayDate), "dd MMM yyyy")}</p>
                                    <p className="text-slate-500">{format(new Date(displayDate), "hh:mm a")}</p>
                                    {lead.last_contacted_by_name && (
                                      <p className="text-xs text-slate-400">by {lead.last_contacted_by_name}</p>
                                    )}
                                    {!lead.last_contacted_at && lastNoteDate && (
                                      <p className="text-xs text-blue-500">from note</p>
                                    )}
                                  </>
                                );
                              }
                              return <span className="text-slate-400">Never</span>;
                            })()}
                          </div>
                        </td>
                        <td>
                          {lead.next_followup_date ? (
                            <div className="text-sm">
                              <p className="text-cyan-700 font-medium">{lead.next_followup_date}</p>
                              <p className="text-slate-500">{lead.next_followup_time || ""}</p>
                            </div>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-green-600 relative z-10"
                              onClick={(e) => { e.stopPropagation(); openWhatsAppChat(lead); }}
                              data-testid={`whatsapp-btn-${lead.lead_id}`}
                            >
                              <MessageCircle className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-blue-600 relative z-10"
                              onClick={(e) => { e.stopPropagation(); setSelectedLead(lead); setShowFollowUpModal(true); }}
                              data-testid={`followup-btn-${lead.lead_id}`}
                            >
                              <CalendarIcon className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between border-t pt-4 mt-4">
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <span>Show</span>
                <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setCurrentPage(1); }}>
                  <SelectTrigger className="w-20 h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                    <SelectItem value="200">200</SelectItem>
                  </SelectContent>
                </Select>
                <span>per page</span>
                <span className="ml-4">
                  Showing {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, totalLeads)} of {totalLeads.toLocaleString()} leads
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                >
                  First
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>
                <span className="px-3 py-1 bg-cyan-50 text-cyan-700 rounded font-medium">
                  Page {currentPage} of {Math.ceil(totalLeads / pageSize) || 1}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.min(Math.ceil(totalLeads / pageSize), p + 1))}
                  disabled={currentPage >= Math.ceil(totalLeads / pageSize)}
                >
                  Next
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(Math.ceil(totalLeads / pageSize))}
                  disabled={currentPage >= Math.ceil(totalLeads / pageSize)}
                >
                  Last
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Lead Detail Modal */}
        <Dialog open={showLeadModal} onOpenChange={setShowLeadModal}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Target className="w-5 h-5 text-cyan-700" />
                Lead Details
              </DialogTitle>
              <DialogDescription>
                View and manage lead information, add notes, and update disposition
              </DialogDescription>
            </DialogHeader>
            
            {selectedLead && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-4">
                {/* Left Column - Lead Info & Actions */}
                <div className="space-y-4">
                  {/* Lead Info Card */}
                  <Card>
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        {editingName ? (
                          <div className="flex items-center gap-2 flex-1">
                            <Input
                              value={editedName}
                              onChange={(e) => setEditedName(e.target.value)}
                              className="h-8"
                              autoFocus
                            />
                            <Button size="sm" variant="ghost" onClick={handleUpdateName}>
                              <CheckCircle className="w-4 h-4 text-green-600" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setEditingName(false)}>
                              <X className="w-4 h-4 text-red-600" />
                            </Button>
                          </div>
                        ) : (
                          <CardTitle className="text-lg flex items-center gap-2">
                            {selectedLead.name}
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              onClick={() => { setEditedName(selectedLead.name); setEditingName(true); }}
                              className="h-6 w-6 p-0"
                            >
                              <Edit2 className="w-3 h-3 text-slate-400" />
                            </Button>
                          </CardTitle>
                        )}
                        {["admin", "super_admin", "team_lead"].includes(userRole) && (
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleDeleteLead(selectedLead.lead_id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <p className="flex items-center gap-2 text-sm">
                        <Phone className="w-4 h-4 text-slate-400" />
                        {selectedLead.phone}
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="h-6 w-6 p-0"
                          onClick={() => { navigator.clipboard.writeText(selectedLead.phone); toast.success("Phone copied!"); }}
                        >
                          <Copy className="w-3 h-3 text-slate-400" />
                        </Button>
                      </p>
                      {selectedLead.email && (
                        <p className="flex items-center gap-2 text-sm">
                          <Mail className="w-4 h-4 text-slate-400" />
                          {selectedLead.email}
                        </p>
                      )}
                      <div className="flex items-center gap-2 text-sm">
                        <Globe className="w-4 h-4 text-slate-400" />
                        <span>Source:</span>
                        {["super_admin", "admin", "team_lead"].includes(userRole) ? (
                          <Select 
                            value={selectedLead.source || "unknown"} 
                            onValueChange={(val) => handleUpdateSource(val)}
                          >
                            <SelectTrigger className="h-7 w-32 text-xs">
                              <SelectValue placeholder="Set source" />
                            </SelectTrigger>
                            <SelectContent>
                              {sourceTabs.filter(s => s.value !== "all").map((s) => (
                                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <span>{selectedLead.source}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <MapPin className="w-4 h-4 text-slate-400" />
                        <span>City:</span>
                        <Select value={selectedLead.city || "not_set"} onValueChange={(val) => handleUpdateCity(val === "not_set" ? "" : val)}>
                          <SelectTrigger className="h-7 w-32 text-xs">
                            <SelectValue placeholder="Set city" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="not_set">Not set</SelectItem>
                            {cities.map((city) => (
                              <SelectItem key={city} value={city}>{city}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <p className="flex items-center gap-2 text-xs text-slate-400">
                        <Clock className="w-3 h-3" />
                        Created: {formatDate(selectedLead.created_at)}
                      </p>
                      <Separator className="my-2" />
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-slate-500">Disposition:</span>
                        {getDispositionBadge(selectedLead.disposition)}
                      </div>
                      
                      {/* IVR Data Section */}
                      {selectedLead.ivr_data && (
                        <>
                          <Separator className="my-2" />
                          <div className="bg-purple-50 rounded-lg p-3">
                            <p className="text-xs font-semibold text-purple-700 mb-2 flex items-center gap-1">
                              <PhoneCall className="w-3 h-3" /> IVR Call Details (CallerDesk)
                            </p>
                            <div className="space-y-1 text-xs max-h-48 overflow-y-auto">
                              {/* Call Timing */}
                              {(selectedLead.ivr_data.start_time || selectedLead.ivr_data.call_time) && (
                                <p><span className="text-slate-500">Start Time:</span> {formatDate(selectedLead.ivr_data.start_time || selectedLead.ivr_data.call_time)}</p>
                              )}
                              {selectedLead.ivr_data.end_time && (
                                <p><span className="text-slate-500">End Time:</span> {formatDate(selectedLead.ivr_data.end_time)}</p>
                              )}
                              {(selectedLead.ivr_data.call_duration || selectedLead.ivr_data.CallDuration) && (
                                <p><span className="text-slate-500">Call Duration:</span> {selectedLead.ivr_data.call_duration || selectedLead.ivr_data.CallDuration}s</p>
                              )}
                              {selectedLead.ivr_data.talk_duration && (
                                <p><span className="text-slate-500">Talk Duration:</span> {selectedLead.ivr_data.talk_duration}s</p>
                              )}
                              
                              {/* Phone Numbers */}
                              {selectedLead.ivr_data.source_number && (
                                <p><span className="text-slate-500">Source Number:</span> {selectedLead.ivr_data.source_number}</p>
                              )}
                              {selectedLead.ivr_data.destination_number && (
                                <p><span className="text-slate-500">Destination:</span> {selectedLead.ivr_data.destination_number}</p>
                              )}
                              {selectedLead.ivr_data.dial_whom_number && (
                                <p><span className="text-slate-500">Dialed To:</span> {selectedLead.ivr_data.dial_whom_number}</p>
                              )}
                              
                              {/* Call Status */}
                              {(selectedLead.ivr_data.status || selectedLead.ivr_data.Status) && (
                                <p><span className="text-slate-500">Status:</span> <Badge variant="outline" className="text-xs">{selectedLead.ivr_data.status || selectedLead.ivr_data.Status}</Badge></p>
                              )}
                              {selectedLead.ivr_data.direction && (
                                <p><span className="text-slate-500">Direction:</span> {selectedLead.ivr_data.direction}</p>
                              )}
                              {selectedLead.ivr_data.call_type && (
                                <p><span className="text-slate-500">Call Type:</span> {selectedLead.ivr_data.call_type}</p>
                              )}
                              
                              {/* IVR Options */}
                              {selectedLead.ivr_data.ivr_options_selected?.length > 0 && (
                                <p><span className="text-slate-500">IVR Options Pressed:</span> <span className="font-medium text-purple-700">{selectedLead.ivr_data.ivr_options_selected.join(' → ')}</span></p>
                              )}
                              {selectedLead.ivr_data.key_press_raw && (
                                <p><span className="text-slate-500">Key Press Raw:</span> {selectedLead.ivr_data.key_press_raw}</p>
                              )}
                              
                              {/* Agent Info */}
                              {selectedLead.ivr_data.agent_email && (
                                <p><span className="text-slate-500">Agent:</span> {selectedLead.ivr_data.agent_email}</p>
                              )}
                              {selectedLead.ivr_data.call_group && (
                                <p><span className="text-slate-500">Call Group:</span> {selectedLead.ivr_data.call_group}</p>
                              )}
                              
                              {/* Hangup Info */}
                              {selectedLead.ivr_data.hangup_cause && (
                                <p><span className="text-slate-500">Hangup Cause:</span> {selectedLead.ivr_data.hangup_cause}</p>
                              )}
                              {selectedLead.ivr_data.hangup_side && (
                                <p><span className="text-slate-500">Hangup Side:</span> {selectedLead.ivr_data.hangup_side}</p>
                              )}
                              
                              {/* IDs */}
                              {selectedLead.ivr_data.call_sid && (
                                <p><span className="text-slate-500">Call SID:</span> <span className="font-mono text-xs">{selectedLead.ivr_data.call_sid}</span></p>
                              )}
                              {selectedLead.ivr_data.campaign_id && (
                                <p><span className="text-slate-500">Campaign ID:</span> {selectedLead.ivr_data.campaign_id}</p>
                              )}
                              
                              {/* Recording */}
                              {selectedLead.ivr_data.recording_url && (
                                <a 
                                  href={selectedLead.ivr_data.recording_url} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:underline block mt-2 font-medium"
                                >
                                  🎵 Listen to Call Recording
                                </a>
                              )}
                              
                              {/* Error if any */}
                              {selectedLead.ivr_data.error_code && (
                                <p className="text-red-600"><span className="text-slate-500">Error:</span> {selectedLead.ivr_data.error_code}</p>
                              )}
                            </div>
                          </div>
                        </>
                      )}
                      
                      {/* Missed Call Data Section */}
                      {selectedLead.missed_call_data && (
                        <>
                          <Separator className="my-2" />
                          <div className="bg-orange-50 rounded-lg p-3">
                            <p className="text-xs font-semibold text-orange-700 mb-2 flex items-center gap-1">
                              <PhoneMissed className="w-3 h-3" /> Missed Call Details
                            </p>
                            <div className="space-y-1 text-xs">
                              {selectedLead.missed_call_data.call_time && (
                                <p><span className="text-slate-500">Call Time:</span> {formatDate(selectedLead.missed_call_data.call_time)}</p>
                              )}
                              {selectedLead.missed_call_data.virtual_number && (
                                <p><span className="text-slate-500">Virtual Number:</span> {selectedLead.missed_call_data.virtual_number}</p>
                              )}
                              {selectedLead.missed_call_data.circle && (
                                <p><span className="text-slate-500">Circle:</span> {selectedLead.missed_call_data.circle}</p>
                              )}
                              {selectedLead.missed_call_data.operator && (
                                <p><span className="text-slate-500">Operator:</span> {selectedLead.missed_call_data.operator}</p>
                              )}
                            </div>
                          </div>
                        </>
                      )}

                      {/* AI Voice Call Recordings Section */}
                      {(selectedLead.call_recordings?.length > 0 || selectedLead.ai_call_recording) && (
                        <>
                          <Separator className="my-2" />
                          <div className="bg-purple-50 rounded-lg p-3">
                            <p className="text-xs font-semibold text-purple-700 mb-2 flex items-center gap-1">
                              <Bot className="w-3 h-3" /> AI Voice Call Recordings
                            </p>
                            <div className="space-y-3">
                              {/* Latest AI call recording */}
                              {selectedLead.ai_call_recording && (
                                <div className="p-2 bg-white rounded border border-purple-200">
                                  <div className="flex items-center justify-between mb-1">
                                    <span className="text-xs font-medium text-purple-700">Latest AI Call</span>
                                    {selectedLead.ai_last_voice_call && (
                                      <span className="text-xs text-slate-500">{formatDate(selectedLead.ai_last_voice_call)}</span>
                                    )}
                                  </div>
                                  <a 
                                    href={selectedLead.ai_call_recording}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-purple-600 hover:underline text-sm flex items-center gap-1"
                                  >
                                    <Headphones className="w-3 h-3" /> Listen to Recording
                                  </a>
                                  {selectedLead.ai_last_call_duration && (
                                    <p className="text-xs text-slate-500 mt-1">Duration: {selectedLead.ai_last_call_duration}s</p>
                                  )}
                                </div>
                              )}
                              
                              {/* AI Transcript */}
                              {selectedLead.ai_last_call_transcript && (
                                <div className="p-2 bg-white rounded border border-purple-200">
                                  <p className="text-xs font-medium text-purple-700 mb-1 flex items-center gap-1">
                                    <FileText className="w-3 h-3" /> Call Transcript
                                  </p>
                                  <p className="text-xs text-slate-600 max-h-32 overflow-y-auto whitespace-pre-wrap">
                                    {selectedLead.ai_last_call_transcript.length > 500 
                                      ? `${selectedLead.ai_last_call_transcript.substring(0, 500)}...` 
                                      : selectedLead.ai_last_call_transcript}
                                  </p>
                                </div>
                              )}
                              
                              {/* AI Insights */}
                              {selectedLead.ai_last_call_insights && (
                                <div className="p-2 bg-white rounded border border-purple-200">
                                  <p className="text-xs font-medium text-purple-700 mb-1 flex items-center gap-1">
                                    <Zap className="w-3 h-3" /> AI Insights
                                  </p>
                                  {selectedLead.ai_last_call_insights.summary && (
                                    <p className="text-xs text-slate-700 mb-2">{selectedLead.ai_last_call_insights.summary}</p>
                                  )}
                                  <div className="flex flex-wrap gap-1">
                                    {selectedLead.ai_last_call_insights.customer_sentiment && (
                                      <Badge variant="outline" className={
                                        selectedLead.ai_last_call_insights.customer_sentiment === 'positive' ? 'border-green-300 text-green-700' :
                                        selectedLead.ai_last_call_insights.customer_sentiment === 'negative' ? 'border-red-300 text-red-700' :
                                        'border-slate-300 text-slate-700'
                                      }>
                                        {selectedLead.ai_last_call_insights.customer_sentiment}
                                      </Badge>
                                    )}
                                    {selectedLead.ai_last_call_insights.appointment_confirmed && (
                                      <Badge className="bg-green-500 text-white">Appointment Booked</Badge>
                                    )}
                                    {selectedLead.ai_last_call_insights.customer_interested === false && (
                                      <Badge className="bg-red-500 text-white">Not Interested</Badge>
                                    )}
                                    {selectedLead.ai_last_call_insights.product_interest && (
                                      <Badge variant="outline" className="border-blue-300 text-blue-700">
                                        {selectedLead.ai_last_call_insights.product_interest} range
                                      </Badge>
                                    )}
                                  </div>
                                </div>
                              )}
                              
                              {/* All recordings list */}
                              {selectedLead.call_recordings?.length > 0 && (
                                <div className="mt-2">
                                  <p className="text-xs text-slate-500 mb-1">All Recordings ({selectedLead.call_recordings.length})</p>
                                  <div className="space-y-1 max-h-24 overflow-y-auto">
                                    {selectedLead.call_recordings.map((rec, idx) => (
                                      <a 
                                        key={idx}
                                        href={rec.recording_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-xs text-purple-600 hover:underline block"
                                      >
                                        {formatDate(rec.created_at)} - Recording
                                      </a>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </>
                      )}
                    </CardContent>
                  </Card>

                  {/* Scheduled Follow-ups Section */}
                  {(selectedLead.follow_ups?.length > 0 || selectedLead.next_followup_date) && (
                    <Card className="border-cyan-200 bg-cyan-50/30">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium flex items-center gap-2 text-cyan-800">
                          <CalendarIcon className="w-4 h-4" />
                          Scheduled Follow-ups
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-2">
                        {selectedLead.next_followup_date && (
                          <div className="p-2 bg-white rounded border border-cyan-200">
                            <div className="flex items-center justify-between">
                              <p className="font-medium text-cyan-700">{selectedLead.next_followup_date} {selectedLead.next_followup_time || ''}</p>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 text-cyan-600 hover:text-cyan-800"
                                onClick={() => {
                                  setFollowUpDate(new Date(selectedLead.next_followup_date));
                                  setFollowUpTime(selectedLead.next_followup_time || "10:00");
                                  setFollowUpReason(selectedLead.next_followup_reason || "");
                                  setShowFollowUpModal(true);
                                }}
                              >
                                <RefreshCw className="w-3 h-3 mr-1" />
                                Reschedule
                              </Button>
                            </div>
                            {selectedLead.next_followup_reason && (
                              <p className="text-xs text-slate-600 mt-1">{selectedLead.next_followup_reason}</p>
                            )}
                          </div>
                        )}
                        {selectedLead.follow_ups?.map((fu, idx) => (
                          <div key={idx} className="p-2 bg-white rounded border border-cyan-200">
                            <div className="flex items-center justify-between">
                              <p className="font-medium text-cyan-700">{fu.scheduled_date} {fu.scheduled_time || ''}</p>
                              {fu.status === 'pending' && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 text-cyan-600 hover:text-cyan-800"
                                  onClick={() => {
                                    setFollowUpDate(new Date(fu.scheduled_date));
                                    setFollowUpTime(fu.scheduled_time || "10:00");
                                    setFollowUpReason(fu.reason || "");
                                    setRescheduleFollowUpId(fu.followup_id);
                                    setShowFollowUpModal(true);
                                  }}
                                >
                                  <RefreshCw className="w-3 h-3 mr-1" />
                                  Reschedule
                                </Button>
                              )}
                            </div>
                            {fu.reason && <p className="text-xs text-slate-600 mt-1">{fu.reason}</p>}
                            <Badge variant="outline" className="text-xs mt-1">{fu.status}</Badge>
                          </div>
                        ))}
                      </CardContent>
                    </Card>
                  )}

                  {/* Update Disposition */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium">Update Disposition</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <Select value={newDisposition} onValueChange={setNewDisposition}>
                        <SelectTrigger data-testid="disposition-select">
                          <SelectValue placeholder="Select disposition" />
                        </SelectTrigger>
                        <SelectContent>
                          {dispositions.map((d) => (
                            <SelectItem key={d.id} value={d.id}>{d.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Textarea
                        placeholder="Add notes about this call..."
                        value={dispositionNotes}
                        onChange={(e) => setDispositionNotes(e.target.value)}
                        rows={2}
                      />
                      <Button 
                        className="w-full bg-cyan-700 hover:bg-cyan-800"
                        onClick={updateDisposition}
                        data-testid="update-disposition-btn"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Update Disposition
                      </Button>
                    </CardContent>
                  </Card>

                  {/* Quick Actions */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium">Quick Actions</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <Button 
                        variant="outline" 
                        className="w-full justify-start text-green-600"
                        onClick={() => openWhatsAppChat(selectedLead)}
                      >
                        <MessageCircle className="w-4 h-4 mr-2" />
                        WhatsApp Chat
                      </Button>
                      <Button 
                        variant="outline" 
                        className="w-full justify-start text-blue-600"
                        onClick={() => setShowFollowUpModal(true)}
                      >
                        <CalendarIcon className="w-4 h-4 mr-2" />
                        Schedule Follow-up
                      </Button>
                      <Button 
                        variant="outline" 
                        className="w-full justify-start text-orange-600"
                        onClick={() => setShowAssignModal(true)}
                      >
                        <Users className="w-4 h-4 mr-2" />
                        Assign Lead
                      </Button>
                      <Button 
                        variant="outline" 
                        className="w-full justify-start text-purple-600"
                        onClick={() => openConvertModal(selectedLead)}
                        disabled={selectedLead.lifecycle_stage === "converted"}
                        data-testid="convert-to-customer-btn"
                      >
                        <UserPlus className="w-4 h-4 mr-2" />
                        Book via SupportHub
                      </Button>
                      {/* Quick disposition buttons */}
                      <div className="grid grid-cols-2 gap-2 pt-2 border-t">
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-purple-700 border-purple-200 hover:bg-purple-50"
                          onClick={async () => {
                            try {
                              await axios.put(`${API}/api/leads/${selectedLead.lead_id}/disposition`, {
                                disposition: "appointment_booked",
                                notes: "Appointment booked via CRM"
                              }, { withCredentials: true });
                              toast.success("Marked as Appointment Booked!");
                              setSelectedLead({ ...selectedLead, disposition: "appointment_booked" });
                              fetchLeads();
                            } catch (e) { toast.error("Failed to update"); }
                          }}
                          data-testid="quick-book-btn"
                        >
                          <CalendarIcon className="w-3 h-3 mr-1" />
                          Appt. Booked
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-green-700 border-green-200 hover:bg-green-50"
                          onClick={async () => {
                            try {
                              await axios.post(`${API}/api/leads/${selectedLead.lead_id}/convert`, {}, { withCredentials: true });
                              toast.success("Lead converted to customer!");
                              setSelectedLead({ ...selectedLead, disposition: "converted", lifecycle_stage: "converted" });
                              fetchLeads();
                            } catch (e) { toast.error("Failed to convert"); }
                          }}
                          disabled={selectedLead.lifecycle_stage === "converted"}
                          data-testid="quick-convert-btn"
                        >
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Converted
                        </Button>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Assigned To Info */}
                  {selectedLead.assigned_to && (
                    <Card className="border-orange-200 bg-orange-50/30">
                      <CardContent className="p-3">
                        <p className="text-xs font-medium text-orange-700 flex items-center gap-2">
                          <Users className="w-3 h-3" />
                          Assigned to: {teamUsers.find(u => u.user_id === selectedLead.assigned_to)?.name || selectedLead.assigned_to_name || selectedLead.assigned_to}
                        </p>
                      </CardContent>
                    </Card>
                  )}
                </div>

                {/* Middle Column - Notes */}
                <div className="space-y-4">
                  <Card className="h-full">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium flex items-center gap-2">
                        <FileText className="w-4 h-4" />
                        Notes
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="flex gap-2">
                        <Textarea
                          placeholder="Add a note..."
                          value={newNote}
                          onChange={(e) => setNewNote(e.target.value)}
                          rows={2}
                          className="flex-1"
                        />
                        <Button 
                          onClick={addNote}
                          className="bg-cyan-700 hover:bg-cyan-800"
                          disabled={!newNote.trim()}
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                      <Separator />
                      <ScrollArea className="h-[300px]">
                        <div className="space-y-3">
                          {(selectedLead.notes || []).slice().reverse().map((note, idx) => (
                            <div key={idx} className={`rounded-lg p-3 ${
                              note.type === 'ai_call_note' || note.type === 'ai_insight' || note.author?.includes('AI') 
                                ? 'bg-purple-50 border border-purple-200' 
                                : 'bg-slate-50'
                            }`}>
                              {/* AI Badge */}
                              {(note.type === 'ai_call_note' || note.type === 'ai_insight' || note.author?.includes('AI')) && (
                                <div className="flex items-center gap-1 mb-2">
                                  <Badge className="bg-purple-600 text-white text-xs px-2 py-0">
                                    <Bot className="w-3 h-3 mr-1" /> AI
                                  </Badge>
                                  {note.type === 'ai_call_note' && (
                                    <Badge variant="outline" className="text-xs border-purple-300 text-purple-700">Voice Call</Badge>
                                  )}
                                  {note.type === 'ai_insight' && (
                                    <Badge variant="outline" className="text-xs border-blue-300 text-blue-700">Insight</Badge>
                                  )}
                                  {note.sentiment && (
                                    <Badge className={`text-xs ${
                                      note.sentiment === 'positive' ? 'bg-green-500' :
                                      note.sentiment === 'negative' ? 'bg-red-500' : 'bg-gray-500'
                                    } text-white`}>
                                      {note.sentiment}
                                    </Badge>
                                  )}
                                </div>
                              )}
                              <p className="text-sm whitespace-pre-wrap">{note.text}</p>
                              <div className="flex items-center justify-between mt-2">
                                <span className={`text-xs ${note.author?.includes('AI') ? 'text-purple-600 font-medium' : 'text-slate-500'}`}>
                                  {note.author}
                                </span>
                                <span className="text-xs text-slate-400">
                                  {formatDate(note.timestamp || note.created_at)}
                                </span>
                              </div>
                              {note.disposition && (
                                <Badge variant="outline" className="mt-1 text-xs">
                                  {note.disposition}
                                </Badge>
                              )}
                            </div>
                          ))}
                          {(!selectedLead.notes || selectedLead.notes.length === 0) && (
                            <p className="text-center text-slate-400 py-4">No notes yet</p>
                          )}
                        </div>
                      </ScrollArea>
                    </CardContent>
                  </Card>
                </div>

                {/* Right Column - Activity Timeline */}
                <div className="space-y-4">
                  <Card className="h-full">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium flex items-center gap-2">
                        <Activity className="w-4 h-4" />
                        Activity Timeline
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ScrollArea className="h-[400px]">
                        <div className="space-y-4">
                          {activities.map((activity, idx) => (
                            <div key={idx} className="flex gap-3">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                                activity.activity_type === "disposition_change" ? "bg-blue-100" :
                                activity.activity_type === "whatsapp_sent" ? "bg-green-100" :
                                activity.activity_type === "followup_created" ? "bg-purple-100" :
                                activity.activity_type === "converted" ? "bg-emerald-100" :
                                "bg-slate-100"
                              }`}>
                                {activity.activity_type === "disposition_change" && <Phone className="w-4 h-4 text-blue-600" />}
                                {activity.activity_type === "whatsapp_sent" && <MessageCircle className="w-4 h-4 text-green-600" />}
                                {activity.activity_type === "followup_created" && <CalendarIcon className="w-4 h-4 text-purple-600" />}
                                {activity.activity_type === "converted" && <CheckCircle className="w-4 h-4 text-emerald-600" />}
                                {activity.activity_type === "note" && <FileText className="w-4 h-4 text-slate-600" />}
                              </div>
                              <div className="flex-1">
                                <p className="text-sm">{activity.description}</p>
                                <p className="text-xs text-slate-400 mt-1">
                                  {activity.created_by_name} • {formatDate(activity.created_at)}
                                </p>
                              </div>
                            </div>
                          ))}
                          {activities.length === 0 && (
                            <p className="text-center text-slate-400 py-4">No activities yet</p>
                          )}
                        </div>
                      </ScrollArea>
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Follow-up Modal */}
        <Dialog open={showFollowUpModal} onOpenChange={(open) => {
          setShowFollowUpModal(open);
          if (!open) {
            setRescheduleFollowUpId(null);
            setFollowUpReason("");
          }
        }}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{rescheduleFollowUpId ? "Reschedule Follow-up" : "Schedule Follow-up"}</DialogTitle>
              <DialogDescription>
                {rescheduleFollowUpId ? "Update the follow-up date and time" : `Set a reminder to follow up with ${selectedLead?.name}`}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <label className="text-sm font-medium">Date</label>
                <Calendar
                  mode="single"
                  selected={followUpDate}
                  onSelect={setFollowUpDate}
                  className="rounded-md border mt-2"
                  disabled={(date) => {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    return date < today;
                  }}
                />
              </div>
              <div>
                <label className="text-sm font-medium">Time</label>
                <Select value={followUpTime} onValueChange={setFollowUpTime}>
                  <SelectTrigger className="mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {["09:00", "10:00", "11:00", "12:00", "14:00", "15:00", "16:00", "17:00", "18:00"].map(t => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Reason / Notes</label>
                <Textarea
                  placeholder="e.g., Discuss pricing options, customer requested callback..."
                  value={followUpReason}
                  onChange={(e) => setFollowUpReason(e.target.value)}
                  className="mt-2"
                  rows={3}
                />
              </div>
              <Button 
                className="w-full bg-cyan-700 hover:bg-cyan-800"
                onClick={createFollowUp}
                disabled={!followUpDate}
              >
                <CalendarIcon className="w-4 h-4 mr-2" />
                {rescheduleFollowUpId ? "Reschedule Follow-up" : "Schedule Follow-up"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* WhatsApp Conversation Modal */}
        <Dialog open={showWhatsAppModal} onOpenChange={setShowWhatsAppModal}>
          <DialogContent className="sm:max-w-lg max-h-[85vh] flex flex-col">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <MessageCircle className="w-5 h-5 text-green-600" />
                WhatsApp Chat
              </DialogTitle>
              <DialogDescription>
                Chat with {selectedLead?.name} ({selectedLead?.phone})
              </DialogDescription>
            </DialogHeader>
            
            {/* Conversation History */}
            <div className="flex-1 min-h-0">
              <ScrollArea className="h-[350px] border rounded-lg bg-slate-50 p-3">
                {loadingConversation ? (
                  <div className="flex items-center justify-center h-full">
                    <RefreshCw className="w-5 h-5 animate-spin text-slate-400" />
                    <span className="ml-2 text-slate-500">Loading conversation...</span>
                  </div>
                ) : conversationHistory.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-slate-500">
                    <MessageCircle className="w-10 h-10 text-slate-300 mb-2" />
                    <p className="text-sm">No conversation history</p>
                    <p className="text-xs text-slate-400">Start a conversation by sending a message</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {conversationHistory.map((msg, idx) => (
                      <div 
                        key={idx} 
                        className={`flex ${msg.direction === 'outbound' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div 
                          className={`max-w-[80%] rounded-lg px-3 py-2 ${
                            msg.direction === 'outbound' 
                              ? 'bg-green-500 text-white' 
                              : 'bg-white border border-slate-200 text-slate-800'
                          }`}
                        >
                          <p className="text-sm whitespace-pre-wrap break-words">{msg.content}</p>
                          <div className={`flex items-center gap-1 mt-1 text-xs ${
                            msg.direction === 'outbound' ? 'text-green-100' : 'text-slate-400'
                          }`}>
                            <span>
                              {new Date(msg.created_at).toLocaleString('en-IN', {
                                day: '2-digit',
                                month: 'short',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </span>
                            {msg.direction === 'outbound' && (
                              <span className="ml-1">
                                {msg.status === 'sent' ? '✓' : msg.status === 'delivered' ? '✓✓' : msg.status === 'read' ? '✓✓' : ''}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </div>

            {/* Message Input */}
            <div className="space-y-3 pt-3 border-t">
              <Textarea
                placeholder="Type your message..."
                value={whatsappMessage}
                onChange={(e) => setWhatsappMessage(e.target.value)}
                rows={2}
                className="resize-none"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendWhatsAppReply();
                  }
                }}
              />
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => fetchConversationHistory(selectedLead?.phone)}
                  disabled={loadingConversation}
                >
                  <RefreshCw className={`w-4 h-4 ${loadingConversation ? 'animate-spin' : ''}`} />
                </Button>
                <Button variant="outline" className="flex-1" onClick={() => setShowWhatsAppModal(false)}>
                  Close
                </Button>
                <Button 
                  className="flex-1 bg-green-600 hover:bg-green-700"
                  onClick={handleSendWhatsAppReply}
                  disabled={!whatsappMessage.trim() || sendingWhatsApp}
                >
                  {sendingWhatsApp ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  Send
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Add Lead Modal */}
        <Dialog open={showAddLeadModal} onOpenChange={setShowAddLeadModal}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Add New Lead</DialogTitle>
              <DialogDescription>
                Manually add a new lead to the system
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <label className="text-sm font-medium">Name *</label>
                <Input
                  placeholder="Customer name"
                  value={newLead.name}
                  onChange={(e) => setNewLead({ ...newLead, name: e.target.value })}
                  className="mt-1"
                  data-testid="add-lead-name"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Phone *</label>
                <Input
                  placeholder="10-digit mobile number"
                  value={newLead.phone}
                  onChange={(e) => setNewLead({ ...newLead, phone: e.target.value })}
                  className="mt-1"
                  data-testid="add-lead-phone"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Email</label>
                <Input
                  placeholder="email@example.com"
                  value={newLead.email}
                  onChange={(e) => setNewLead({ ...newLead, email: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Source</label>
                <Select value={newLead.source} onValueChange={(val) => setNewLead({ ...newLead, source: val })}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {sourceTabs.filter(s => s.value !== "all").map(s => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Assign to</label>
                <Select value={newLead.assigned_to || "auto"} onValueChange={(val) => setNewLead({ ...newLead, assigned_to: val === "auto" ? "" : val })}>
                  <SelectTrigger className="mt-1" data-testid="add-lead-assign-to">
                    <SelectValue placeholder="Auto-assign (recommended)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">Auto-assign</SelectItem>
                    {assignableUsers.map((u) => (
                      <SelectItem key={u.user_id} value={u.user_id}>
                        {u.name} ({u.role})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1">
                  {["super_admin", "admin"].includes(userRole) 
                    ? "Leave empty to auto-assign based on allocation rules"
                    : userRole === "team_lead"
                    ? "Assign to yourself or your team members"
                    : "Lead will be assigned to you"
                  }
                </p>
              </div>
              <div>
                <label className="text-sm font-medium">City</label>
                <Select value={newLead.city || "not_specified"} onValueChange={(val) => setNewLead({ ...newLead, city: val === "not_specified" ? "" : val })}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select city" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="not_specified">Not specified</SelectItem>
                    {cities.map((city) => (
                      <SelectItem key={city} value={city}>{city}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Initial Notes</label>
                <Textarea
                  placeholder="Any initial notes about this lead..."
                  value={newLead.notes}
                  onChange={(e) => setNewLead({ ...newLead, notes: e.target.value })}
                  className="mt-1"
                />
              </div>
              <Button
                className="w-full bg-cyan-700 hover:bg-cyan-800"
                onClick={handleAddLead}
                data-testid="submit-add-lead"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Add Lead
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Assign Lead Modal */}
        <Dialog open={showAssignModal} onOpenChange={setShowAssignModal}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Assign Lead</DialogTitle>
              <DialogDescription>
                Assign {selectedLead?.name} to a team member
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <label className="text-sm font-medium">Select Agent</label>
                <Select value={assignToUser} onValueChange={setAssignToUser}>
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="Choose an agent" />
                  </SelectTrigger>
                  <SelectContent>
                    {assignableUsers.map((u) => (
                      <SelectItem key={u.user_id} value={u.user_id}>
                        {u.name} ({u.role})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                className="w-full bg-cyan-700 hover:bg-cyan-800"
                onClick={handleAssignLead}
                disabled={!assignToUser}
              >
                <Users className="w-4 h-4 mr-2" />
                Assign Lead
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Bulk Assign Modal */}
        <Dialog open={showBulkAssignModal} onOpenChange={setShowBulkAssignModal}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-cyan-700" />
                Bulk Assign Leads
              </DialogTitle>
              <DialogDescription>
                Assign {selectedLeadIds.length} selected leads to a team member
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <label className="text-sm font-medium">Select Agent</label>
                <Select value={bulkAssignUser} onValueChange={setBulkAssignUser}>
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="Choose an agent" />
                  </SelectTrigger>
                  <SelectContent>
                    {assignableUsers.map((u) => (
                      <SelectItem key={u.user_id} value={u.user_id}>
                        {u.name} ({u.role})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="bg-slate-50 p-3 rounded-lg">
                <p className="text-sm text-slate-600">
                  <strong>{selectedLeadIds.length}</strong> leads will be reassigned to the selected agent.
                </p>
              </div>
              <Button
                className="w-full bg-cyan-700 hover:bg-cyan-800"
                onClick={handleBulkAssign}
                disabled={!bulkAssignUser || bulkAssigning}
                data-testid="bulk-assign-submit-btn"
              >
                {bulkAssigning ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Assigning...
                  </>
                ) : (
                  <>
                    <Users className="w-4 h-4 mr-2" />
                    Assign {selectedLeadIds.length} Leads
                  </>
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Convert to Customer Modal */}
        <Dialog open={showConvertModal} onOpenChange={setShowConvertModal}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-purple-600" />
                Convert to Customer - Book Appointment
              </DialogTitle>
              <DialogDescription>
                Create appointment in Supporthub for {selectedLead?.name}
              </DialogDescription>
            </DialogHeader>

            {loadingMasters ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-6 h-6 animate-spin text-cyan-700" />
                <span className="ml-2 text-slate-600">Loading Supporthub data...</span>
              </div>
            ) : (
              <div className="space-y-4 mt-4">
                {/* Customer Details */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-slate-700">Customer Name *</label>
                    <Input
                      value={convertForm.name}
                      onChange={(e) => setConvertForm({...convertForm, name: e.target.value})}
                      placeholder="Customer name"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700">Mobile Number *</label>
                    <Input
                      value={convertForm.mobileno}
                      onChange={(e) => setConvertForm({...convertForm, mobileno: e.target.value.replace(/\D/g, "").slice(0, 10)})}
                      placeholder="10-digit mobile number"
                      maxLength={10}
                    />
                  </div>
                </div>

                {/* Clinic Selection */}
                <div>
                  <label className="text-sm font-medium text-slate-700">Clinic *</label>
                  <Select value={convertForm.clinic} onValueChange={(v) => setConvertForm({...convertForm, clinic: v})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select clinic..." />
                    </SelectTrigger>
                    <SelectContent>
                      <ScrollArea className="h-48">
                        {supporthubMasters?.clinics?.map((c) => (
                          <SelectItem key={c.clinic_id} value={String(c.clinic_id)}>
                            {c.clinic_name}
                          </SelectItem>
                        ))}
                      </ScrollArea>
                    </SelectContent>
                  </Select>
                </div>

                {/* Appointment Type */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-slate-700">Appointment Type *</label>
                    <Select value={convertForm.appointmenttype} onValueChange={(v) => setConvertForm({...convertForm, appointmenttype: v})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select type..." />
                      </SelectTrigger>
                      <SelectContent>
                        {supporthubMasters?.appointment_types?.map((a) => (
                          <SelectItem key={a.appointment_id} value={String(a.appointment_id)}>
                            {a.appointment_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700">Appointment Date & Time *</label>
                    <Input
                      type="datetime-local"
                      value={convertForm.appointment}
                      onChange={(e) => {
                        // Convert to YYYY-MM-DD HH:MM format
                        const dt = e.target.value.replace("T", " ");
                        setConvertForm({...convertForm, appointment: dt});
                      }}
                    />
                  </div>
                </div>

                {/* Consultant & Source */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-slate-700">Consultant *</label>
                    <Select value={convertForm.consultant} onValueChange={(v) => setConvertForm({...convertForm, consultant: v})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select consultant..." />
                      </SelectTrigger>
                      <SelectContent>
                        <ScrollArea className="h-48">
                          {supporthubMasters?.consultants?.map((c) => (
                            <SelectItem key={c.consultant_id} value={String(c.consultant_id)}>
                              {c.consultant_name}
                            </SelectItem>
                          ))}
                        </ScrollArea>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700">Lead Source (Subbrand)</label>
                    <Select value={convertForm.subbrand} onValueChange={(v) => setConvertForm({...convertForm, subbrand: v})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select source..." />
                      </SelectTrigger>
                      <SelectContent>
                        <ScrollArea className="h-48">
                          {supporthubMasters?.subsources?.map((s) => (
                            <SelectItem key={s.sub_source_id} value={String(s.sub_source_id)}>
                              {s.sub_source_name}
                            </SelectItem>
                          ))}
                        </ScrollArea>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Subject & Comment */}
                <div>
                  <label className="text-sm font-medium text-slate-700">Subject</label>
                  <Input
                    value={convertForm.subject}
                    onChange={(e) => setConvertForm({...convertForm, subject: e.target.value})}
                    placeholder="Brief subject"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Comments</label>
                  <Textarea
                    value={convertForm.comment}
                    onChange={(e) => setConvertForm({...convertForm, comment: e.target.value})}
                    placeholder="Additional notes..."
                    rows={3}
                  />
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-4 border-t">
                  <Button variant="outline" className="flex-1" onClick={() => setShowConvertModal(false)}>
                    Cancel
                  </Button>
                  <Button 
                    className="flex-1 bg-purple-600 hover:bg-purple-700"
                    onClick={handleConvertToCustomer}
                    disabled={converting}
                  >
                    {converting ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <UserPlus className="w-4 h-4 mr-2" />
                    )}
                    Create Appointment
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Duplicates Modal */}
        <Dialog open={showDuplicatesModal} onOpenChange={setShowDuplicatesModal}>
          <DialogContent className="sm:max-w-3xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Copy className="w-5 h-5 text-orange-600" />
                Duplicate Leads ({duplicates.length} groups found)
              </DialogTitle>
              <DialogDescription>
                Review and merge duplicate leads based on phone number
              </DialogDescription>
            </DialogHeader>
            {loadingDuplicates ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="w-6 h-6 animate-spin text-cyan-600" />
              </div>
            ) : duplicates.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <CheckCircle className="w-12 h-12 mx-auto text-green-500 mb-2" />
                No duplicate leads found!
              </div>
            ) : (
              <div className="space-y-4 mt-4">
                {duplicates.map((group, idx) => (
                  <Card key={idx} className="border-orange-200">
                    <CardHeader className="py-2 bg-orange-50">
                      <CardTitle className="text-sm flex items-center justify-between">
                        <span>Phone: {group._id} ({group.count} leads)</span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="py-2">
                      <div className="space-y-2">
                        {group.leads.map((lead, lidx) => (
                          <div 
                            key={lead.lead_id} 
                            className={`flex items-center justify-between p-2 rounded ${lidx === 0 ? 'bg-green-50 border border-green-200' : 'bg-slate-50'}`}
                          >
                            <div className="flex-1">
                              <span className="font-medium">{lead.name}</span>
                              <span className="text-xs text-slate-500 ml-2">
                                {lead.source} | {lead.disposition || 'No disposition'} | {lead.created_at?.slice(0, 10)}
                              </span>
                              {lidx === 0 && <Badge className="ml-2 bg-green-600">Primary</Badge>}
                            </div>
                            {lidx > 0 && (
                              <Button 
                                size="sm" 
                                variant="destructive"
                                onClick={() => handleDeleteLead(lead.lead_id)}
                              >
                                <Trash2 className="w-3 h-3 mr-1" />
                                Delete
                              </Button>
                            )}
                          </div>
                        ))}
                      </div>
                      {group.leads.length > 1 && (
                        <Button 
                          className="w-full mt-2 bg-orange-600 hover:bg-orange-700"
                          size="sm"
                          onClick={() => handleMergeDuplicates(
                            group.leads[0].lead_id, 
                            group.leads.slice(1).map(l => l.lead_id)
                          )}
                        >
                          Merge All Into Primary (Keep First, Delete Rest)
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
